import { useCallback, useEffect, useRef, useState } from 'react';
import { Banner, BlockStack, Button, InlineStack, Select, Text } from '@shopify/polaris';
import { useAuth } from '../../App';
import { useApi } from '../../hooks/useApi';
import { courierTrackingIntervals, type CourierTrackingSchedule as Schedule } from '../../../../packages/shared/courier-tracking-schedule';
import type { CourierBooking } from './booking-types';

const date = (value: string) => new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`).toLocaleString();
export default function CourierTrackingSchedule({ shipmentId, booking, disabled }: { shipmentId: string; booking: CourierBooking; disabled: boolean }) {
  const { user } = useAuth(); const { fetchApi, loading, error } = useApi();
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [interval, setInterval] = useState('30'); const [busy, setBusy] = useState(false); const [message, setMessage] = useState('');
  const generation = useRef(0);
  const path = `/api/shipments/${shipmentId}/courier/bookings/${booking.id}/tracking/schedule`;
  const load = useCallback(async () => {
    const version = ++generation.current;
    const result = await fetchApi<{ schedule: Schedule }>(path);
    if (version !== generation.current) return;
    setSchedule(result?.schedule ?? null);
    if (result) setInterval(String(result.schedule.interval_minutes));
  }, [fetchApi, path]);
  useEffect(() => { void load(); return () => { generation.current++; }; }, [load]);
  const save = async (enabled: boolean) => {
    if (!schedule || !user || busy || disabled) return;
    const version = ++generation.current; setBusy(true); setMessage('');
    const result = await fetchApi<{ schedule: Schedule }>(path, { method: 'PUT', body: JSON.stringify({
      expected_user_id: user.id, request_id: crypto.randomUUID(), booking_revision: booking.revision,
      revision: schedule.revision, enabled, interval_minutes: Number(interval),
    }) });
    if (version !== generation.current) return;
    if (result) { setSchedule(result.schedule); setMessage(enabled ? 'Scheduled checks are enabled.' : 'Scheduled checks are paused.'); }
    else { await load(); setMessage('The save reply was not received. Current settings have been reloaded. Check them before trying again.'); }
    setBusy(false);
  };
  return <BlockStack gap="200">
    <Text as="h5" variant="headingSm">Scheduled tracking checks</Text>
    <Text as="p">Check the original courier account while you are away. Checks stop after 30 days, when this shipment is fully received, or if its booking, connection or staff access changes. You can pause at any time.</Text>
    {error && <Banner tone="warning">{error}</Banner>}
    {message && <Text as="p">{message}</Text>}
    {schedule && <>
      <Text as="p">{schedule.enabled ? 'Enabled' : 'Paused'}{schedule.next_check_at ? ` · Next check due ${date(schedule.next_check_at)}` : ''}</Text>
      {schedule.enabled && schedule.ends_at && <Text as="p">Stops by {date(schedule.ends_at)}. Courier limits or outages can delay checks.</Text>}
      {schedule.last_checked_at && <Text as="p">Last scheduled attempt: {date(schedule.last_checked_at)}</Text>}
      {schedule.problem && <Banner tone="warning">{schedule.problem}</Banner>}
      <Select label="Check interval" value={interval} options={courierTrackingIntervals.map(n => ({label:`Every ${n} minutes`,value:String(n)}))}
        onChange={setInterval} disabled={disabled || busy || loading} />
      <InlineStack gap="200">
        <Button onClick={() => save(true)} disabled={disabled || busy || loading || booking.status !== 'booked'} loading={busy}>{schedule.enabled ? 'Save tracking schedule' : 'Enable scheduled tracking'}</Button>
        {schedule.enabled && <Button onClick={() => save(false)} disabled={disabled || busy || loading}>Pause scheduled tracking</Button>}
      </InlineStack>
    </>}
    <Button onClick={load} disabled={busy || loading || disabled}>Reload tracking schedule</Button>
  </BlockStack>;
}
